/**
 * Stock Chart Viewer - Main Application
 * 株価チャートビューア
 */

// Import modules
// Import modules
import { state, resetState, saveState, loadState } from './state.js';
import { elements } from './ui/dom.js';
import { initChart, clearChart } from './chart/init.js';
import { updateIndicators } from './chart/indicators.js'; // toggleIndicator handled in events
import { fetchStockData } from './api/index.js';
import {
    renderCachedDataList,
    clearAllCache
} from './cache/storage.js';
import { fetchApiUsage, updateApiUsageUI } from './cache/usage-tracker.js';
import {
    renderWatchlist,
    removeFromWatchlist
} from './features/watchlist.js';
import { initDashboard, renderNewsFeed } from './features/dashboard.js';
import { initAIAssistant, analyzeCurrentStock } from './features/ai-assistant.js';
import { initPortfolio } from './features/portfolio.js';
import { initAlerts } from './features/alerts.js';
import { initMultiChart } from './features/multi-chart.js';
import { restoreDrawingsForSymbol } from './chart/drawing.js';
import { initToolbar } from './ui/toolbar.js';
import { initPaperTrading } from './features/paper-trading.js';
import { exportToCSV } from './features/export.js';
import { updateNewsFeed } from './features/news.js';
import { applyTranslations, getLanguage } from './i18n.js';
import { initPWA } from './features/pwa.js';
import {
    showLoading,
    hideLoading,
    showError,
    hideError,
    showToast,
    updateUI,
    updatePeriodButtons,
    updateHeaderCacheMode,
    updateFooterInfo
} from './ui/ui-manager.js';
import { API_PROVIDER } from './config.js';
import { AuthService } from './auth/auth-service.js';
import { initEventListeners } from './events.js';

// ============================================
// Core Logic Handlers
// ============================================

// Handle Indicator Warnings
document.addEventListener('indicator-warning', (e) => {
    showToast(e.detail.message, 'warning');
});

async function searchStock(symbol) {
    if (!symbol || symbol.trim() === '') {
        showError('ティッカーシンボルを入力してください。');
        return;
    }

    symbol = symbol.trim().toUpperCase();
    state.currentSymbol = symbol;

    showLoading();
    hideError();

    try {
        const data = await fetchStockData(symbol, state.currentPeriod);
        state.stockData = data;

        // Dashboard Updates
        if (elements.newsSection) elements.newsSection.classList.remove('hidden');
        renderNewsFeed(symbol);

        // Restore Drawings
        restoreDrawingsForSymbol(symbol);

        updateUI(data);
    } catch (error) {
        showError(error.message);
        console.error('Stock fetch error:', error);
    } finally {
        hideLoading();
    }
}

async function changePeriod(period) {
    if (!state.currentSymbol) return;

    state.currentPeriod = period;
    updatePeriodButtons();

    showLoading();

    try {
        const data = await fetchStockData(state.currentSymbol, period);
        state.stockData = data;
        updateUI(data);
    } catch (error) {
        showError(error.message);
    } finally {
        hideLoading();
    }
}

function resetToHome() {
    resetState();

    elements.symbolInput.value = '';
    elements.stockInfo.classList.add('hidden');
    elements.newsSection.classList.add('hidden');
    elements.statsPanel.classList.add('hidden');
    elements.welcomeMessage.classList.remove('hidden');
    elements.cacheIndicator.classList.add('hidden');

    clearChart();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    elements.symbolInput.focus();

    console.log('Reset to home');
}

// Comparison Helper (needed for events)
async function handleAddComparison() {
    if (!elements.compareInput) return;
    const symbol = elements.compareInput.value.trim().toUpperCase();
    if (!symbol) return;

    if (symbol === state.currentSymbol) {
        showError('メインチャートと同じシンボルです。');
        return;
    }
    if (state.comparisonSeries.some(s => s.symbol === symbol)) {
        showError('このシンボルは既に追加されています。');
        return;
    }

    elements.compareBtn.disabled = true;
    elements.compareBtn.textContent = '...';

    try {
        const data = await fetchStockData(symbol, state.currentPeriod);
        document.dispatchEvent(new CustomEvent('add-comparison', { detail: { symbol, data: data.candleData } }));
        elements.compareInput.value = '';
    } catch (error) {
        showError(`比較データの取得に失敗: ${error.message}`);
    } finally {
        elements.compareBtn.disabled = false;
        elements.compareBtn.textContent = '+';
    }
}


// Handle Indicator Visibility Toggles (Panes) (Keep global listener here or move to events if logic-free)
// Ideally this logic belongs in ui-js manager or indicators.js, but keeping it here for now or moving to events?
// It's strictly UI logic based on event. Let's keep it here or move to events? 
// Actually events.js is for *registering* listeners. The handler logic acts on UI. 
// Moving this specific complex handler to events.js might bloat it. 
// For "light" refactor, let's keep logic here but registered via initEventListeners? 
// No, initEventListeners handles element clicks. 
// Custom document events like 'indicator-toggle' and 'indicator-warning' are global business logic.
// Let's keep them here or move them to a proper controller setup later.
// For now, I'll leave them to minimize risk, as requested "light" refactor.

document.addEventListener('indicator-toggle', (e) => {
    const { indicator } = e.detail;

    if (indicator === 'rsi') {
        if (state.indicators.rsi) {
            elements.rsiChartContainer.classList.remove('hidden');
        } else {
            elements.rsiChartContainer.classList.add('hidden');
        }
    } else if (indicator === 'macd') {
        if (state.indicators.macd) {
            elements.macdChartContainer.classList.remove('hidden');
        } else {
            elements.macdChartContainer.classList.add('hidden');
        }
    }
    window.dispatchEvent(new Event('resize'));
});


// ============================================
// Initialization
// ============================================

document.addEventListener('DOMContentLoaded', async () => {
    console.log('Stock Chart Viewer initialized (modular + events)');
    elements.symbolInput.focus();

    renderWatchlist();
    const usage = await fetchApiUsage();
    updateFooterInfo(API_PROVIDER, usage.limit || 800);
    renderCachedDataList();

    loadState();

    // Apply Language
    applyTranslations();

    // Initialize Sidebar
    import('./ui/sidebar.js').then(module => {
        module.initSidebar();
    });

    // Initialize Events
    initEventListeners({
        searchStock,
        changePeriod,
        resetToHome,
        analyzeCurrentStock,
        handleAddComparison, // New helper for comparison event
        refreshCurrentStock: () => searchStock(state.currentSymbol), // Wrapper for refresh
        exportCsv: exportToCSV
    });

    // Initialize UI based on state
    if (state.currentMode === 'CRYPTO') {
        elements.modeToggleWrapper.classList.add('crypto-active');
        elements.symbolInput.placeholder = 'Crypto Symbol (e.g. BTC, ETH)';
        elements.modeOptions.forEach(opt => {
            opt.classList.toggle('active', opt.dataset.mode === 'CRYPTO');
        });
    }

    elements.cacheOnlyToggle.checked = state.cacheOnlyMode;
    elements.storageModeSelect.value = state.storageMode;
    updateHeaderCacheMode();

    // Restore Indicator Buttons
    const indicators = ['sma20', 'sma50', 'bb', 'rsi', 'macd'];
    indicators.forEach(ind => {
        const btn = document.getElementById(`${ind}-btn`);
        if (btn && state.indicators[ind]) {
            btn.classList.add('active');
            if (['rsi', 'macd'].includes(ind)) {
                document.dispatchEvent(new CustomEvent('indicator-toggle', { detail: { indicator: ind } }));
            }
        }
    });

    // Auto-load last symbol or default to AAPL
    // Fix: If saved symbol is SPX or NDX (paid only), reset to AAPL
    if (state.currentSymbol === 'SPX' || state.currentSymbol === 'NDX') {
        state.currentSymbol = 'AAPL';
    }

    if (state.currentSymbol) {
        elements.symbolInput.value = state.currentSymbol;
        searchStock(state.currentSymbol);
    } else {
        // Initial default load
        state.currentSymbol = 'AAPL';
        elements.symbolInput.value = 'AAPL';
        searchStock('AAPL');
    }

    // Initialize AI Assistant
    initAIAssistant();

    // Initialize Dashboard
    initDashboard();

    // Initialize Portfolio
    initPortfolio();

    // Initialize Alerts
    initAlerts();

    // Initialize Paper Trading
    initPaperTrading();

    // Initialize Multi-Chart
    initMultiChart();

    // Initialize Toolbar
    initToolbar();

    // Initialize PWA
    initPWA();

    // Initialize Auth (SSO Pilot)
    AuthService.init();
});

